import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();
    const fallbackUrl = this.dataset.productUrl;

    const baseUrl = productLink?.href || fallbackUrl;
    if (!baseUrl) return '';

    const url = new URL(baseUrl, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
      if (!productGrid) return;
    }

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#openQuickAddModal();
  };

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#disableZoomInModal(modalContent);
    this.#syncVariantSelection(modalContent);
    this.#moveBuyButtonsToDialog(modalContent);
  }

  /**
   * Removes zoom functionality from slideshow slides in the modal
   * @param {Element} modalContent - The modal content element
   */
  #disableZoomInModal(modalContent) {
    // Remove on:click attributes from slideshow-slides that trigger zoom
    const slideshowSlides = modalContent.querySelectorAll('slideshow-slide[on\\:click*="zoom"]');
    for (const slide of slideshowSlides) {
      slide.removeAttribute('on:click');
    }

    // Also remove zoom buttons if any
    const zoomButtons = modalContent.querySelectorAll('.product-media-container__zoom-button');
    for (const button of zoomButtons) {
      button.remove();
    }
  }

  /**
   * Moves buy-buttons-block to dialog level to avoid being trapped by animation transforms
   * @param {Element} modalContent - The modal content element
   */
  #moveBuyButtonsToDialog(modalContent) {
    const dialog = modalContent.closest('dialog.quick-add-modal');
    if (!dialog) return;

    const existingFloatingButtons = dialog.querySelector(':scope > .buy-buttons-block');
    if (existingFloatingButtons) {
      existingFloatingButtons.remove();
    }

    const buyButtonsBlock = modalContent.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    buyButtonsBlock.classList.add('buy-buttons-block--floating');
    dialog.appendChild(buyButtonsBlock);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  #ensureMainDialogRef() {
    const main = /** @type {HTMLDialogElement | null} */ (this.querySelector(':scope > dialog.quick-add-modal'));
    if (main && this.refs?.dialog !== main) this.refs.dialog = main;
  }

  showDialog() {
    this.#ensureMainDialogRef();
    super.showDialog();
  }

  constructor() {
    super();
    const parentCloseDialog = this.closeDialog.bind(this);
    this.closeDialog = async () => {
      this.#ensureMainDialogRef();
      return parentCloseDialog();
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateViewProductLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('pointerdown', this.#handleCloseButtonPointerDown, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #handleCloseButtonPointerDown = (/** @type {PointerEvent} */ event) => {
    const closeBtn = this.refs?.closeButton ?? this.querySelector('[ref="closeButton"]');
    const el = Array.isArray(closeBtn) ? closeBtn[0] : closeBtn;
    if (!(el instanceof Element)) return;
    const path = event.composedPath?.() ?? (event.target ? [event.target] : []);
    const hitCloseButton = path.some((node) => node === el || (node instanceof Element && el.contains(node)));
    if (hitCloseButton) {
      event.preventDefault();
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateViewProductLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.buy-buttons-block__view-product-link')
    );
    const viewProductLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.buy-buttons-block__view-product-link')
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewProductLink) viewProductLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    // Sync the floating buy-buttons-block with the variant update
    this.#syncFloatingBuyButtonsForm(event);
  };

  /**
   * Syncs the floating buy-buttons-block form with variant updates
   * @param {CustomEvent} event - The variant update event
   */
  #syncFloatingBuyButtonsForm = (/** @type {CustomEvent} */ event) => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const floatingBuyButtons = dialog.querySelector(':scope > .buy-buttons-block');
    if (!floatingBuyButtons) return;

    const blockIdClass = Array.from(floatingBuyButtons.classList).find(
      (c) => c.startsWith('buy-buttons-block--') && c !== 'buy-buttons-block--floating'
    );
    const blockId = blockIdClass?.replace('buy-buttons-block--', '') ?? '';
    const updatedBuyButtons = blockId
      ? event.detail.data.html?.querySelector(`.buy-buttons-block--${CSS.escape(blockId)}`)
      : event.detail.data.html?.querySelector('.buy-buttons-block');
    if (!updatedBuyButtons) return;

    const updatedProductFormButtons = updatedBuyButtons.querySelector('.product-form-buttons');
    const updatedNotifyMeButton = updatedProductFormButtons?.querySelector('.notify-me-button');
    const updatedAddToCartComponent = updatedProductFormButtons?.querySelector('add-to-cart-component');
    const clonedNotifyMe = updatedNotifyMeButton?.cloneNode(true);
    const clonedAddToCart = updatedAddToCartComponent?.cloneNode(true);

    morph(floatingBuyButtons, updatedBuyButtons);

    floatingBuyButtons.classList.add('buy-buttons-block--floating');

    for (const bisModal of floatingBuyButtons.querySelectorAll('back-in-stock-modal')) {
      bisModal.remove();
    }

    const productFormButtons = floatingBuyButtons.querySelector('.product-form-buttons');
    if (productFormButtons && (clonedNotifyMe || clonedAddToCart)) {
      const currentNotifyMeButton = productFormButtons.querySelector('.notify-me-button');
      const currentAddToCartComponent = productFormButtons.querySelector('add-to-cart-component');

      if (clonedNotifyMe && currentAddToCartComponent && !currentNotifyMeButton) {
        currentAddToCartComponent.replaceWith(clonedNotifyMe);
      } else if (clonedAddToCart && currentNotifyMeButton) {
        currentNotifyMeButton.replaceWith(clonedAddToCart);
      } else if (clonedNotifyMe && currentNotifyMeButton) {
        try {
          morph(currentNotifyMeButton, updatedNotifyMeButton);
        } catch {
          currentNotifyMeButton.replaceWith(clonedNotifyMe);
        }
      } else if (clonedAddToCart && !currentAddToCartComponent && !currentNotifyMeButton) {
        const quantitySelector = productFormButtons.querySelector('.quantity-selector');
        if (quantitySelector?.nextSibling) {
          productFormButtons.insertBefore(clonedAddToCart, quantitySelector.nextSibling ?? null);
        } else {
          productFormButtons.appendChild(clonedAddToCart);
        }
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
